import { useHeatReportContext } from "@/pages/heat-report/context/HeatReportContext";
import {
  BorderedTablePaper,
  OutlinedTextarea,
} from "@/pages/heat-report/styles/HeatReportStyledComponents";
import { getMomentTz, useUTLSettingsContext } from "@dexteel/mesf-core";
import { Box, Grid, TextField } from "@mui/material";
import { AssetCard } from "../../../controls/AssetCard";
import { DurationFieldDE } from "@/shared/data-entries/common/DurationFieldDE";
import DurationInput from "@/shared/data-entries/common/DurationInput";
import { CheckboxFieldDE } from "@/shared/data-entries/common/CheckboxFieldDE";
import { get } from "lodash-es";

const moment = getMomentTz();
export const HeaderData = () => {
  const {
    state: { eafProcessData, heat, delays },
  } = useHeatReportContext();

  const { state } = useUTLSettingsContext();
  const targetTTT = parseInt(get(state.settings, "EAF_TARGET_TTT", 60));

  return (
    <BorderedTablePaper elevation={0}>
      <Box padding={1}>
        <Grid item xs={12} container spacing={2} key={eafProcessData.ProcessId}>
          {/* Column 1 - Asset Card */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            container
            key={`${eafProcessData.ProcessId}-column-1`}
          >
            <Grid item xs={12} key={`${heat.HeatId}-asset`}>
              <AssetCard
                assetId={eafProcessData?.AssetId}
                assetName={eafProcessData?.AssetName}
              />
            </Grid>
          </Grid>

          {/* Column 2 */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            container
            key={`${eafProcessData.ProcessId}-column-2`}
          >
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={6} key="start-time">
                  <TextField
                    label="Start Time"
                    value={
                      eafProcessData?.Start
                        ? moment(eafProcessData.Start).format(
                            "HH:mm - MM/DD/YYYY",
                          )
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} key="start-heat">
                  <TextField
                    label="Start Heat"
                    value={
                      eafProcessData?.StartHeat
                        ? moment(eafProcessData.StartHeat).format(
                            "HH:mm - MM/DD/YYYY",
                          )
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} key="tap-time">
                  <TextField
                    label="Tap Time"
                    value={
                      eafProcessData?.End
                        ? moment(eafProcessData.End).format(
                            "HH:mm - MM/DD/YYYY",
                          )
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} key="tap-to-tap">
                  <TextField
                    label={"Tap To Tap [min]"}
                    value={eafProcessData?.TapToTap?.toFixed(1) ?? ""}
                    variant="outlined"
                    fullWidth
                    slotProps={{
                      input: {
                        style: {
                          color:
                            targetTTT && eafProcessData?.TapToTap > targetTTT
                              ? "red"
                              : "inherit",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} key="delay-time">
                  <TextField
                    label="Delay Time [min]"
                    value={
                      delays && delays.length > 0
                        ? delays
                            .reduce(
                              (sum, d) => (sum + (d.Duration ?? 0)) / 60,
                              0,
                            )
                            .toFixed(1)
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} key="steel-weight">
                  <TextField
                    label="Steel Weight [t]"
                    value={eafProcessData?.SteelWeight?.toFixed(1) ?? ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Column 3 */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            container
            key={`${eafProcessData.ProcessId}-column-3`}
          >
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={6} key="mwh">
                  <TextField
                    label="Energy [mwh]"
                    value={eafProcessData?.MWH ?? ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} key="kwh-per-ton">
                  <TextField
                    label="KWH/Ton"
                    value={
                      eafProcessData?.KWHPerTon
                        ? parseFloat(eafProcessData.KWHPerTon.toFixed(1))
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <DurationInput
                    label={"Power On [hh:mm]"}
                    initMinutes={eafProcessData?.PowerOn ?? ""}
                    readOnly
                  />
                </Grid>
                <Grid item xs={6}>
                  <DurationInput
                    label={"Power Off [hh:mm]"}
                    initMinutes={eafProcessData?.PowerOff ?? ""}
                    readOnly
                  />
                </Grid>
                <Grid item xs={6} key="foreman">
                  <TextField
                    label="Foreman"
                    value={eafProcessData?.Foreman ?? ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} key="operator">
                  <TextField
                    label="Operator"
                    value={eafProcessData?.Operator ?? ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Column 4 */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            container
            key={`${eafProcessData.ProcessId}-column-4`}
          >
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={6} key="heel">
                  <TextField
                    label="Heel"
                    value={eafProcessData?.Heel ?? ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} key="lime">
                  <TextField
                    label="Lime"
                    value={eafProcessData?.Lime ?? ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Total O2 Inj"
                    value={
                      eafProcessData?.InjectedO2
                        ? parseFloat(eafProcessData.InjectedO2.toFixed(2))
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} key="carbon">
                  <TextField
                    label="Total Carbon Inj"
                    value={eafProcessData?.InjectedCarbon ?? ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} key="non-free-open">
                  <CheckboxFieldDE
                    label="Non Free Open"
                    value={eafProcessData?.IsNonFreeOpen}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} style={{ height: "100%" }} />
              </Grid>
            </Grid>
          </Grid>

          {/* Column 5 */}
          <Grid
            item
            xs={6}
            sm={6}
            md={1}
            container
            key={`${eafProcessData.ProcessId}-column-5`}
          >
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12} container direction="row">
                  <Grid item xs={12}>
                    <CheckboxFieldDE
                      label="Electrode 1"
                      value={eafProcessData?.Electrode1}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <CheckboxFieldDE
                      label="Electrode 2"
                      value={eafProcessData?.Electrode2}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <CheckboxFieldDE
                      label="Electrode 3"
                      value={eafProcessData?.Electrode3}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Column 6 */}
          <Grid
            item
            xs={6}
            sm={6}
            md={3}
            container
            key={`${eafProcessData.ProcessId}-column-6`}
          >
            <Grid item xs={12} md={12}>
              <OutlinedTextarea
                placeholder="Notes"
                rows={6}
                value={eafProcessData?.Comments ?? ""}
                disabled
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </BorderedTablePaper>
  );
};
